import { apiRequest } from '../config/api';
import { generateFlightTrackCsv, downloadCsv } from './flightMeasurements';

export interface Aircraft {
  _id: string;
  identification: string;
  type: string;
}

export interface Coordinate {
  latitude: number;
  longitude: number;
  altitude: number;
  timestamp: string;
}

export interface MeasurementResult {
  timestamp: string;
  channel: string;
  value: number;
  aircraftId: string;
  flightTrackId: string;
}

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}:${m.toString().padStart(2, '0')} h`;
};

export const getAircraftTypeName = (type: string): string => {
  const mapping: Record<string, string> = {
    balloon: 'Ballonfahrt', ballon: 'Ballonfahrt',
    glider: 'Segelflug', segelflugzeug: 'Segelflug',
    plane: 'Motorflug', motorflugzeug: 'Motorflug',
    helicopter: 'Hubschrauberflug', hubschrauber: 'Hubschrauberflug',
  };
  return mapping[type?.toLowerCase()] || type || 'Flug';
};

export const calculateDistance = (coordinates: Coordinate[]): number => {
  if (coordinates.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < coordinates.length; i++) {
    const lat1 = coordinates[i - 1].latitude;
    const lon1 = coordinates[i - 1].longitude;
    const lat2 = coordinates[i].latitude;
    const lon2 = coordinates[i].longitude;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return total;
};

export const getMaxAltitude = (coordinates: Coordinate[]): number => {
  if (coordinates.length === 0) return 0;
  return Math.max(...coordinates.map(c => c.altitude));
};

export interface FlightExportOptions {
  dateEnd?: string;
  coordinates?: Coordinate[];
  measurements?: MeasurementResult[];
}

export const handleFlightExport = async (
  id: string,
  identification: string,
  dateStart: string,
  basePath: string,
  format: 'csv' | 'pdf',
  options?: FlightExportOptions,
): Promise<void> => {
  if (format === 'pdf') {
    const response = await apiRequest(`${basePath}/${id}/export/pdf`);
    if (!response.ok) throw new Error('Export fehlgeschlagen');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flug-${identification}-${dateStart.slice(0, 10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return;
  }

  if (format === 'csv') {
    let coordinates = options?.coordinates;
    let dateEnd = options?.dateEnd;
    let measurements = options?.measurements;

    // Fetch flight details (coordinates, dateEnd) if missing
    if (!coordinates) {
      try {
        const flightRes = await apiRequest(`${basePath}/${id}?includeCoordinates=true`);
        if (flightRes.ok) {
          const flightData = await flightRes.json();
          coordinates = flightData.coordinates;
          if (!dateEnd) dateEnd = flightData.dateEnd;
        }
      } catch (err) {
        console.warn('Could not fetch flight track for CSV export:', err);
      }
    }

    // Fetch measurements if missing
    if (!measurements) {
      try {
        const sensorRes = await apiRequest(`/sensor-data/${id}`);
        if (sensorRes.ok) {
          const sensorData = await sensorRes.json();
          measurements = sensorData.measurements || [];
        }
      } catch (err) {
        console.warn('Could not fetch sensor data for CSV export:', err);
        measurements = [];
      }
    }

    const csvContent = generateFlightTrackCsv({
      aircraftRegistration: identification,
      dateStart,
      dateEnd,
      coordinates: coordinates || [],
      measurements: measurements || [],
    });

    const filename = `flug-${identification}-${dateStart.slice(0, 10)}.csv`;
    downloadCsv(filename, csvContent);
  }
};

